import { useState, type ReactNode } from 'react';

import { Aviso, Campo, Painel } from '../../comum';
import type { PropsSecao } from './comum';

/**
 * Conformidade contínua: o que é violação e o que fazer com ela.
 */
export function SecaoSentinela({ c, podeEditar, acao, salvar }: PropsSecao): ReactNode {
  const [sentinela, setSentinela] = useState(c.sentinela);

  return (
    <Painel
      titulo="Sentinela"
      sub="Guard-rails da remediação. Estes limites valem por cima do que qualquer regra pedir."
    >
      {sentinela.killSwitch && (
        <Aviso tipo="erro">
          <strong>Kill switch acionado.</strong> Nenhuma varredura roda e nenhuma execução sai
          enquanto ele estiver ligado.
        </Aviso>
      )}

      <label style={{ display: 'flex', gap: 8 }}>
        <input
          type="checkbox"
          checked={sentinela.automatico}
          disabled={!podeEditar}
          onChange={(e) => setSentinela({ ...sentinela, automatico: e.target.checked })}
        />
        <span>Varredura de conformidade automática</span>
      </label>

      <div className="grade2">
        <Campo rotulo="Intervalo da varredura (minutos)">
          <input
            type="number"
            min={5}
            value={sentinela.intervaloMin}
            disabled={!podeEditar}
            onChange={(e) =>
              setSentinela({
                ...sentinela,
                intervaloMin: Number(e.target.value),
              })
            }
          />
        </Campo>
        <Campo rotulo="Tamanho da onda" ajuda="Máquinas por lote. A primeira onda é o canário.">
          <input
            type="number"
            min={1}
            value={sentinela.tamanhoDaOndaPadrao}
            disabled={!podeEditar}
            onChange={(e) =>
              setSentinela({
                ...sentinela,
                tamanhoDaOndaPadrao: Number(e.target.value),
              })
            }
          />
        </Campo>
        <Campo
          rotulo="Disjuntor: falhas consecutivas"
          ajuda="Falhas seguidas que param tudo. Script quebrado deve custar uma onda, não o parque."
        >
          <input
            type="number"
            min={1}
            value={sentinela.limiteDeFalhasConsecutivas}
            disabled={!podeEditar}
            onChange={(e) =>
              setSentinela({
                ...sentinela,
                limiteDeFalhasConsecutivas: Number(e.target.value),
              })
            }
          />
        </Campo>
        <Campo
          rotulo="Blast radius máximo"
          ajuda="Teto de máquinas por remediação, independente do escopo da regra."
        >
          <input
            type="number"
            min={1}
            value={sentinela.blastRadiusMaximo}
            disabled={!podeEditar}
            onChange={(e) =>
              setSentinela({
                ...sentinela,
                blastRadiusMaximo: Number(e.target.value),
              })
            }
          />
        </Campo>
        <Campo
          rotulo="Fila padrão do KACE"
          ajuda="Onde abrir o chamado de autorização quando a regra não define. Vazio = o produto recusa em vez de escolher a fila errada."
        >
          <input
            type="number"
            min={1}
            placeholder="não configurada"
            value={sentinela.filaPadraoIdNoKace ?? ''}
            disabled={!podeEditar}
            onChange={(e) =>
              setSentinela({
                ...sentinela,
                filaPadraoIdNoKace: e.target.value === '' ? null : Number(e.target.value),
              })
            }
          />
        </Campo>
      </div>

      <label style={{ display: 'flex', gap: 8 }}>
        <input
          type="checkbox"
          checked={sentinela.janelaManutencao.ativa}
          disabled={!podeEditar}
          onChange={(e) =>
            setSentinela({
              ...sentinela,
              janelaManutencao: {
                ...sentinela.janelaManutencao,
                ativa: e.target.checked,
              },
            })
          }
        />
        <span>Só remediar dentro da janela de manutenção</span>
      </label>

      {sentinela.janelaManutencao.ativa && (
        <div className="grade2">
          <Campo rotulo="Início (hora)">
            <input
              type="number"
              min={0}
              max={23}
              value={sentinela.janelaManutencao.inicioHora}
              disabled={!podeEditar}
              onChange={(e) =>
                setSentinela({
                  ...sentinela,
                  janelaManutencao: {
                    ...sentinela.janelaManutencao,
                    inicioHora: Number(e.target.value),
                  },
                })
              }
            />
          </Campo>
          <Campo
            rotulo="Fim (hora)"
            ajuda="Início maior que fim atravessa a meia-noite (ex.: 20 → 6)."
          >
            <input
              type="number"
              min={0}
              max={23}
              value={sentinela.janelaManutencao.fimHora}
              disabled={!podeEditar}
              onChange={(e) =>
                setSentinela({
                  ...sentinela,
                  janelaManutencao: {
                    ...sentinela.janelaManutencao,
                    fimHora: Number(e.target.value),
                  },
                })
              }
            />
          </Campo>
        </div>
      )}

      <div className="linha">
        <button
          className="primario encolhe"
          disabled={acao.executando || !podeEditar}
          onClick={() => salvar('sentinela', sentinela, 'Sentinela')}
        >
          Salvar
        </button>
        <button
          className="perigo encolhe"
          disabled={acao.executando || !podeEditar}
          onClick={() =>
            salvar(
              'sentinela',
              { ...sentinela, killSwitch: !sentinela.killSwitch },
              sentinela.killSwitch ? 'Kill switch desligado' : 'Kill switch acionado',
            )
          }
        >
          {sentinela.killSwitch ? 'Desligar kill switch' : 'Acionar kill switch'}
        </button>
        <span className="cresce" />
      </div>
    </Painel>
  );
}
